import {
  chargedAsListed,
  currencyFor,
  type Currency,
} from '../../shared/billing/catalogue';

/**
 * Which price list a visitor most likely pays from, worked out in the browser.
 * The public pages are one copy for everyone, which a CDN can keep, so the
 * server draws one default and the script moves it. Checkout picks the
 * currency by the visitor's address, which the script asks the server for
 * (GET /api/geo/country, then listFor()); this guess is what it shows until
 * that answers, and what it keeps when the address names no country.
 *
 * The browser's time zone says where the device is, so it is asked first.
 * The language list only speaks when the zone names no place (UTC, which
 * privacy modes report), because plenty of browsers in Auckland or Berlin
 * say "en-US".
 *
 * Only the countries with a list of their own are written down: the four
 * with a hand-set currency and the euro's members, as catalogue.ts has them.
 * Every other place gets the euro list, which checkout converts. The spec
 * holds this table against countries-and-timezones, which the server already
 * uses and which is too large to send to a browser for one guess.
 */
const ZONES: Record<string, string> = {
  US:
    'America/Adak America/Anchorage America/Atka America/Boise America/Chicago ' +
    'America/Denver America/Detroit America/Fort_Wayne ' +
    'America/Indiana/Indianapolis America/Indiana/Knox America/Indiana/Marengo ' +
    'America/Indiana/Petersburg America/Indiana/Tell_City America/Indiana/Vevay ' +
    'America/Indiana/Vincennes America/Indiana/Winamac America/Indianapolis ' +
    'America/Juneau America/Kentucky/Louisville America/Kentucky/Monticello ' +
    'America/Knox_IN America/Los_Angeles America/Louisville America/Menominee ' +
    'America/Metlakatla America/New_York America/Nome ' +
    'America/North_Dakota/Beulah America/North_Dakota/Center ' +
    'America/North_Dakota/New_Salem America/Phoenix America/Shiprock ' +
    'America/Sitka America/Yakutat Pacific/Honolulu US/Alaska US/Aleutian ' +
    'US/Arizona US/Central US/East-Indiana US/Eastern US/Hawaii ' +
    'US/Indiana-Starke US/Michigan US/Mountain US/Pacific',
  CA:
    'America/Atikokan America/Blanc-Sablon America/Cambridge_Bay ' +
    'America/Coral_Harbour America/Creston America/Dawson America/Dawson_Creek ' +
    'America/Edmonton America/Fort_Nelson America/Glace_Bay America/Goose_Bay ' +
    'America/Halifax America/Inuvik America/Iqaluit America/Moncton ' +
    'America/Montreal America/Nipigon America/Pangnirtung America/Rainy_River ' +
    'America/Rankin_Inlet America/Regina America/Resolute America/St_Johns ' +
    'America/Swift_Current America/Thunder_Bay America/Toronto ' +
    'America/Vancouver America/Whitehorse America/Winnipeg America/Yellowknife ' +
    'Canada/Atlantic Canada/Central Canada/Eastern Canada/Mountain ' +
    'Canada/Newfoundland Canada/Pacific Canada/Saskatchewan Canada/Yukon',
  GB: 'Europe/Belfast Europe/London',
  AU:
    'Antarctica/Macquarie Australia/ACT Australia/Adelaide Australia/Brisbane ' +
    'Australia/Broken_Hill Australia/Canberra Australia/Currie Australia/Darwin ' +
    'Australia/Eucla Australia/Hobart Australia/LHI Australia/Lindeman ' +
    'Australia/Lord_Howe Australia/Melbourne Australia/NSW Australia/North ' +
    'Australia/Perth Australia/Queensland Australia/South Australia/Sydney ' +
    'Australia/Tasmania Australia/Victoria Australia/West Australia/Yancowinna',
  AT: 'Europe/Vienna',
  BE: 'Europe/Brussels',
  BG: 'Europe/Sofia',
  CY: 'Asia/Famagusta Asia/Nicosia Europe/Nicosia',
  DE: 'Europe/Berlin Europe/Busingen',
  EE: 'Europe/Tallinn',
  ES: 'Africa/Ceuta Atlantic/Canary Europe/Madrid',
  FI: 'Europe/Helsinki',
  FR: 'Europe/Paris',
  GR: 'Europe/Athens',
  HR: 'Europe/Zagreb',
  IE: 'Europe/Dublin',
  IT: 'Europe/Rome',
  LT: 'Europe/Vilnius',
  LU: 'Europe/Luxembourg',
  LV: 'Europe/Riga',
  MT: 'Europe/Malta',
  NL: 'Europe/Amsterdam',
  PT: 'Atlantic/Azores Atlantic/Madeira Europe/Lisbon',
  SI: 'Europe/Ljubljana',
  SK: 'Europe/Bratislava',
};

const ZONE_COUNTRY = new Map(
  Object.entries(ZONES).flatMap(([country, zones]) =>
    zones.split(' ').map((zone) => [zone, country] as const),
  ),
);

export interface CurrencyGuess {
  currency: Currency;
  /**
   * True when checkout will not charge the listed amounts as they stand: the
   * visitor is somewhere Stripe converts the euro price for.
   */
  converted: boolean;
}

/** The list for someone in `country`, an ISO code such as "GB". */
export const listFor = (country: string | null): CurrencyGuess => ({
  currency: currencyFor(country),
  converted: !chargedAsListed(country),
});

/**
 * A zone that names a place: "Europe/Paris", not "UTC" or "Etc/GMT+5". The
 * old one-word names (GB, Japan, EST) are left out with them: no browser
 * reports one unless the machine was set up by hand to say so.
 */
function namesPlace(zone: string): boolean {
  return zone.includes('/') && !zone.startsWith('Etc/');
}

/** "GB" from "en-GB" or "zh-Hant-HK"; null for a language with no region. */
function regionOf(language: string): string | null {
  const match = /^[a-z]{2,3}(?:-[a-z]{4})?-([a-z]{2})(?:-|$)/i.exec(language);
  return match ? match[1].toUpperCase() : null;
}

/**
 * The country the browser's zone is in, for /get-started's first guess at
 * "You live in" while the server works out a better one (GET
 * /api/geo/country). Only the zones in the table above; null for the rest.
 */
export function countryOfZone(zone: string | null): string | null {
  return zone && namesPlace(zone) ? (ZONE_COUNTRY.get(zone) ?? null) : null;
}

/**
 * `zone` is Intl's `resolvedOptions().timeZone` and `languages` is
 * `navigator.languages`. Null when neither says anything, and the page then
 * keeps the list the server drew.
 */
export function guessCurrency(
  zone: string | null,
  languages: readonly string[],
): CurrencyGuess | null {
  if (zone && namesPlace(zone)) {
    return listFor(ZONE_COUNTRY.get(zone) ?? null);
  }
  for (const language of languages) {
    const region = regionOf(language);
    if (region) return listFor(region);
  }
  return null;
}
